import {
  Control,
  FieldValues,
  Path,
  useController,
  useForm,
} from 'react-hook-form'
import { useEffect, useState } from 'react'
import { z, ZodObject } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu'
import { cn } from 'components/UI/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
} from 'components/UI/BaseDropdown/dropdown-base'
import {
  FILTER_POPOVER_BODY,
  FILTER_POPOVER_CONTENT_BASE,
  FILTER_POPOVER_FOOTER,
} from './popoverClasses'
import { FilterButton } from '../components/FilterButton'
import { FilterCloseButton } from '../components/FilterCloseButton'
import { useRegisterFilterKey } from '../components/FilterModal'
import FbButton from 'components/FbUI/FbButton'
import { FilterStore } from '../FilterStore'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { NumericFormat } from 'react-number-format'

interface NamedRangeFilterProps {
  min?: number
  max?: number
  store: FilterStore
}

export function RangeFilterWithMinMax(
  props: NamedRangeFilterProps & BaseRangeFilterProps
) {
  const { min, max, ...rest } = props

  const zodObject = z.object({
    min: z
      .number()
      .min(min ?? 1)
      .max(max ?? 100)
      .optional()
      .nullable(),
    max: z
      .number()
      .min(min ?? 1)
      .max(max ?? 100)
      .optional()
      .nullable(),
  })

  return <RangeFilter {...rest} zodObject={zodObject} />
}

export function ChainSizeRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="Chain Size"
      filterKey="chain_count"
      store={props.store}
    />
  )
}

export function AvgRatingRangeFilter(props: NamedRangeFilterProps) {
  const zodObject = z.object({
    min: z.number().min(0).max(5).optional().nullable(),
    max: z.number().min(0).max(5).optional().nullable(),
  })

  return (
    <RangeFilter
      title="Average Rating"
      filterKey="avg_rating"
      decimalScale={2}
      store={props.store}
      zodObject={zodObject}
    />
  )
}
// Tells FilterModal's alphabetical sort how to bucket this composite — the
// title is hardcoded inside, so the modal can't read it off the JSX prop.
AvgRatingRangeFilter.sortMeta = { title: 'Average Rating' }

export function ReviewCountRangeFilter(props: NamedRangeFilterProps) {
  const zodObject = z.object({
    min: z.number().min(0).max(1000000000).optional().nullable(),
    max: z.number().min(0).max(1000000000).optional().nullable(),
  })

  return (
    <RangeFilter
      title="Review Count"
      filterKey="reviews_count"
      store={props.store}
      zodObject={zodObject}
    />
  )
}
ReviewCountRangeFilter.sortMeta = { title: 'Review Count' }

export function InstagramFollowersRangeFilter(props: NamedRangeFilterProps) {
  const zodObject = z.object({
    min: z.number().min(0).max(1000000000).optional().nullable(),
    max: z.number().min(0).max(1000000000).optional().nullable(),
  })

  return (
    <RangeFilter
      title="Instagram Followers"
      filterKey="instagram_followers"
      store={props.store}
      zodObject={zodObject}
    />
  )
}
InstagramFollowersRangeFilter.sortMeta = { title: 'Instagram Followers' }

export function LTVTotalRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="Lifetime Total Value"
      filterKey="total_ltv"
      store={props.store}
      prefix="$"
      decimalScale={2}
    />
  )
}

export function BrandLTVRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="Lifetime Brand Value"
      filterKey="brand_ltv"
      store={props.store}
      prefix="$"
      decimalScale={2}
    />
  )
}

export function RevenueLTVRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="Lifetime Revenue Value"
      filterKey="revenue_ltv"
      store={props.store}
      prefix="$"
      decimalScale={2}
    />
  )
}

export function PoundsPerYearRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="1yr Volume"
      filterKey="pounds_per_year"
      store={props.store}
    />
  )
}

export function TaroPlusTaboRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="1yr Total Value"
      filterKey="one_year_total_value"
      store={props.store}
      prefix="$"
      decimalScale={2}
    />
  )
}

export function TaroRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="1yr Revenue Value"
      filterKey="taro"
      store={props.store}
      prefix="$"
      decimalScale={2}
    />
  )
}

export function TaboRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="1yr Brand Value"
      filterKey="tabo"
      store={props.store}
      prefix="$"
      decimalScale={2}
    />
  )
}

export function DealCountRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="Deal Count"
      filterKey="deal_count"
      store={props.store}
    />
  )
}

export function NoteCountRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="Note Count"
      filterKey="note_count"
      store={props.store}
    />
  )
}
NoteCountRangeFilter.sortMeta = { title: 'Note Count' }

export function ContactCountRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="Contact Count"
      filterKey="contact_count"
      store={props.store}
    />
  )
}
ContactCountRangeFilter.sortMeta = { title: 'Contact Count' }

export function MedianHouseholdIncomeRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="Median Household Income"
      filterKey="median_hhi"
      store={props.store}
      prefix="$"
    />
  )
}

export function PopDensityRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="Population Density"
      filterKey="pop_density"
      store={props.store}
    />
  )
}

export function HighHouseholdIncomeRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="High Household Income"
      filterKey="hh_gt100k"
      store={props.store}
      decimalScale={2}
    />
  )
}

export function UnemploymentRateRangeFilter(props: NamedRangeFilterProps) {
  return (
    <RangeFilter
      title="Unemployment Rate"
      filterKey="unemployment_rate"
      store={props.store}
      decimalScale={2}
    />
  )
}

export interface BaseRangeFilterProps {
  title: string
  filterKey: string

  zodObject?: ZodObject<any>
  decimalScale?: number
  store: FilterStore
  minAllowNegative?: boolean
  maxAllowNegative?: boolean
  disabled?: boolean
  disabledTooltipText?: string
  /** Currency / unit prefix shown inside each input (e.g. "$"). When set,
   *  inputs render a separated prefix column matching the DS cost-input. */
  prefix?: string
}

export function RangeFilter({
  title,
  filterKey,
  zodObject,
  decimalScale = 0,
  store,
  minAllowNegative = true,
  maxAllowNegative = true,
  disabled = false,
  disabledTooltipText,
  prefix,
}: BaseRangeFilterProps) {
  const [open, setOpen] = useState(false)
  const {
    pendingRangeFilters,
    setPendingRangeFilters,
    clearPendingRangeFilter,
    registerFilterTitle,
    registerFilterPrefix,
    registerFilterDecimalScale,
  } = store()
  const current = pendingRangeFilters?.[filterKey!]

  const { control, watch, reset, handleSubmit } = useForm({
    defaultValues: {
      min: parseFloat(current?.min?.toString() ?? '') || null,
      max: parseFloat(current?.max?.toString() ?? '') || null,
    },
    resolver: zodObject ? zodResolver(zodObject) : undefined,
  })

  // Reactively-tracked form values + derived range-validation flag. Hoisted
  // here (vs reading inside an IIFE in the popover body) so the popover JSX
  // stays flat and matches DateRangeFilterWithPresets' shape.
  const minValue = watch('min')
  const maxValue = watch('max')
  const rangeError =
    minValue != null && maxValue != null && Number(maxValue) < Number(minValue)

  function saveFilters() {
    if (minValue != null || maxValue != null) {
      setPendingRangeFilters(
        filterKey,
        minValue ?? undefined,
        maxValue ?? undefined
      )
    } else if (isRangeSet) {
      // Reset → Apply path: inputs are blank but a value was previously
      // committed. Drop the filter instead of no-op'ing, otherwise the old
      // range stays pending and the chip never clears.
      clearPendingRangeFilter(filterKey)
    }

    setOpen(false)
  }

  const clearFilters = () => {
    reset({
      min: null,
      max: null,
    })
    clearPendingRangeFilter(filterKey)
  }

  const _handleSubmit = handleSubmit(() => {
    saveFilters()
    setOpen(false)
  })

  const isRangeSet = current?.min != null || current?.max != null

  // Abbreviate only when the number is an exact multiple of 1K / 1M / 1B —
  // a "ballpark" value where the trailing zeros carry no information. Specific
  // numbers (e.g. 12,345) keep all digits with thousands separators so the
  // user sees what they actually entered; the pill ellipses if it overflows.
  // decimalScale governs sub-1K rendering (e.g. ratings show as "4.5", not "5").
  const formatCompact = (n: number) => {
    const abs = Math.abs(n)
    let body: string
    if (abs >= 1_000_000_000 && n % 1_000_000_000 === 0) {
      body = `${n / 1_000_000_000}B`
    } else if (abs >= 1_000_000 && n % 1_000_000 === 0) {
      body = `${n / 1_000_000}M`
    } else if (abs >= 1_000 && n % 1_000 === 0) {
      body = `${n / 1_000}K`
    } else if (abs >= 1_000) {
      // Honor decimalScale on the locale-formatted fallback so currency
      // values like 50500.50 render as "50,500.50" (not "50,500.5"
      // dropped by the default Number → string conversion).
      body = n.toLocaleString(undefined, {
        minimumFractionDigits: decimalScale,
        maximumFractionDigits: decimalScale,
      })
    } else {
      body = decimalScale > 0 ? n.toFixed(decimalScale) : String(n)
    }
    return prefix ? `${prefix}${body}` : body
  }
  const triggerLabel = (() => {
    const min = current?.min
    const max = current?.max
    if (min == null && max == null) return title
    if (min != null && max == null) return `${title}: ≥ ${formatCompact(min)}`
    if (min == null && max != null) return `${title}: ≤ ${formatCompact(max)}`
    return `${title}: ${formatCompact(min!)} – ${formatCompact(max!)}`
  })()

  useEffect(() => {
    registerFilterTitle(filterKey, title)
    if (prefix) registerFilterPrefix(filterKey, prefix)
    if (decimalScale > 0) registerFilterDecimalScale(filterKey, decimalScale)
  }, [])

  useRegisterFilterKey(filterKey)

  if (disabled) {
    const filterButton = (
      <div className="h-full w-full relative opacity-50 cursor-not-allowed">
        <div className={'h-full w-full flex align-center pointer-events-none'}>
          <FilterButton
            open={false}
            isActive={false}
            title={title}
            onClick={() => {}}
          />
        </div>
      </div>
    )

    if (disabledTooltipText) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`${filterKey}-disabled-tooltip`}>
              {disabledTooltipText}
            </Tooltip>
          }
        >
          {filterButton}
        </OverlayTrigger>
      )
    }

    return filterButton
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          // Rebuild the form from the committed store value on each open
          // so a previously-cancelled edit doesn't linger. Mirrors the
          // SelectFilter / DateRangeFilter fix.
          reset({
            min: parseFloat(current?.min?.toString() ?? '') || null,
            max: parseFloat(current?.max?.toString() ?? '') || null,
          })
        }
        setOpen(nextOpen)
      }}
    >
      <div className="h-full w-full relative">
        <div className={'h-full w-full flex align-center'}>
          <DropdownMenuTrigger asChild>
            <FilterButton
              open={open}
              isActive={isRangeSet}
              title={triggerLabel}
            />
          </DropdownMenuTrigger>
          <FilterCloseButton
            isActive={isRangeSet}
            open={open}
            onClick={() => clearFilters()}
          />
        </div>
      </div>

      <DropdownMenuContent
        forceMount={true}
        side={'bottom'}
        className={cn(FILTER_POPOVER_CONTENT_BASE, 'w-[280px]')}
        collisionPadding={12}
        sideOffset={6}
      >
        {/* Inline range-validation error: only when both fields are entered
            and max is less than min. Doesn't block the user from typing —
            just guards Apply and shows a small DS error message. */}
        <div className={FILTER_POPOVER_BODY}>
          <div className="flex items-center gap-2">
            <RangeNumericInput
              control={control}
              name="min"
              placeholder={prefix === '$' ? '0.00' : 'Min'}
              decimalScale={decimalScale}
              allowNegative={minAllowNegative}
              prefix={prefix}
              error={rangeError}
            />
            <span className="text-sm text-neutral-500">
              {prefix === '$' ? 'to' : '–'}
            </span>
            <RangeNumericInput
              control={control}
              name="max"
              placeholder={prefix === '$' ? '0.00' : 'Max'}
              decimalScale={decimalScale}
              allowNegative={maxAllowNegative}
              prefix={prefix}
              error={rangeError}
            />
          </div>

          {rangeError && (
            <p className="m-0 mt-2 text-xs leading-4 text-error-strong">
              Max must be ≥ min
            </p>
          )}
        </div>

        <div
          className={cn(
            FILTER_POPOVER_FOOTER,
            'mt-4 items-center justify-between gap-2'
          )}
        >
          <button
            type="button"
            onClick={() => reset({ min: null, max: null })}
            disabled={minValue == null && maxValue == null}
            className={cn(
              'text-sm font-medium transition-colors',
              minValue == null && maxValue == null
                ? 'text-neutral-400 cursor-not-allowed'
                : 'text-brand-active hover:text-brand-bright'
            )}
          >
            Reset
          </button>
          <div className="flex gap-2">
            <FbButton
              size="sm"
              variant="secondary"
              type="button"
              onClick={() => setOpen(false)}
            >
              Cancel
            </FbButton>
            <FbButton
              size="sm"
              type="button"
              className="!bg-brand-bright !text-white hover:!bg-brand-active active:!bg-brand-active disabled:!bg-neutral-100 disabled:!text-neutral-400 disabled:!border-neutral-100 disabled:hover:!bg-neutral-100"
              onClick={_handleSubmit}
              // Stays enabled when a filter is currently applied (`isRangeSet`)
              // so the user can Reset to clear the inputs and then Apply the
              // empty state to drop the filter. Without this, Reset traps
              // the user — the inputs go blank but Apply greys out, so the
              // old range stays applied until they Cancel out.
              disabled={
                rangeError ||
                (!isRangeSet && minValue == null && maxValue == null)
              }
            >
              Apply
            </FbButton>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Inline DS numeric input used inside the range popover. Mirrors the
// playground's MenuInput / MenuCostInput: 36px height, rounded-lg, tabular
// nums, brand-active focus ring. When `prefix` is set (e.g. "$") the input
// renders a separated prefix column.
function RangeNumericInput<T extends FieldValues>({
  control,
  name,
  placeholder,
  decimalScale = 0,
  allowNegative = true,
  prefix,
  error,
}: {
  control: Control<T, any>
  name: Path<T>
  placeholder: string
  decimalScale?: number
  allowNegative?: boolean
  prefix?: string
  error?: boolean
}) {
  const {
    field: { onChange, value },
  } = useController({ name, control })
  const [focused, setFocused] = useState(false)

  const wrapperBase =
    'flex-1 min-w-0 flex items-center h-9 rounded-lg border bg-white transition-shadow'
  // Error state takes precedence over focus/idle — gives the input the same
  // red border + red focus ring the validation message refers to.
  const wrapperState = error
    ? focused
      ? 'border-error-strong shadow-[0_0_0_4px_rgba(220,38,38,0.2)]'
      : 'border-error-strong'
    : focused
      ? 'border-brand-active shadow-[0_0_0_4px_rgba(8,116,67,0.2)]'
      : 'border-neutral-500 hover:border-neutral-600'

  if (prefix) {
    return (
      <div className={cn(wrapperBase, 'pl-2.5 pr-1.5 gap-2', wrapperState)}>
        <span className="text-sm text-neutral-500">{prefix}</span>
        <span className="self-stretch w-px bg-neutral-200 shrink-0" />
        <NumericFormat
          value={value ?? ''}
          placeholder={placeholder}
          thousandSeparator
          allowNegative={allowNegative}
          decimalScale={decimalScale}
          allowedDecimalSeparators={decimalScale ? ['.'] : []}
          onValueChange={(v, src) => {
            if (src.source !== 'event') return
            onChange(v.value === '' ? null : v.floatValue)
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="flex-1 min-w-0 border-none outline-none bg-transparent text-sm text-neutral-800 tabular-nums placeholder-neutral-400"
        />
      </div>
    )
  }

  return (
    <NumericFormat
      value={value ?? ''}
      placeholder={placeholder}
      thousandSeparator
      allowNegative={allowNegative}
      decimalScale={decimalScale}
      allowedDecimalSeparators={decimalScale ? ['.'] : []}
      onValueChange={(v, src) => {
        if (src.source !== 'event') return
        onChange(v.value === '' ? null : v.floatValue)
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      className={cn(
        'flex-1 min-w-0 h-9 px-3 rounded-lg outline-none',
        'text-sm text-neutral-800 tabular-nums placeholder-neutral-400',
        'bg-white border transition-shadow',
        wrapperState
      )}
    />
  )
}
